import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import AdminLogin from "./AdminLogin";
import CustomAiConfigurationForm from "./CustomAiConfigurationForm";
import CodexConfigurationForm from "./CodexConfigurationForm";
import EventEvaluationConfigurationForm from "./EventEvaluationConfigurationForm";
import WorkerPoolPanel from "./WorkerPoolPanel";
import ReferencePacksPanel from "./ReferencePacksPanel";
import { AuditPanel, HealthStatusPanel, SyncStatusPanel } from "./AdminStatusPanels";
import UserRevenuePanel from "./UserRevenuePanel";
import AdminSectionBoundary from "./AdminSectionBoundary";
import ReferralAdminPanel from "./ReferralAdminPanel";
import PayoutAdminPanel from "./PayoutAdminPanel";
import PatternResearchTab from "../research/PatternResearchTab";
import "./AdminPanel.css";
import "./AdminEmbedded.css";

const ADMIN_GROUPS = Object.freeze([
  { label: "Users & Revenue", items: [["users", "Users"], ["referrals", "Referrals"], ["payouts", "Payouts"]] },
  { label: "AI Configuration", items: [["custom-ai", "Custom AI"], ["codex", "Codex"], ["event-validation", "Event Validation"], ["workers", "Workers"], ["reference-packs", "Reference Packs"]] },
  { label: "Research", items: [["pattern-research", "Pattern Research"]] },
  { label: "System", items: [["health", "Health"], ["sync", "Sync"], ["audit", "Audit"]] },
]);
const TABS = ADMIN_GROUPS.flatMap((group) => group.items.map(([id, label]) => [id, label, label]));

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function backgroundElements(dialog) {
  const elements = [];
  let current = dialog;
  while (current?.parentElement) {
    const parent = current.parentElement;
    for (const sibling of parent.children) {
      if (sibling !== current) elements.push(sibling);
    }
    if (parent === document.body) break;
    current = parent;
  }
  return [...new Set(elements)];
}

const modalIsolationOwners = new Map();
const modalIsolationRecords = new Map();
let modalIsolationObserver = null;
let modalIsolationBaseFocus = null;
let modalIsolationBaseFocusCaptured = false;
let modalIsolationLease = 0;
let modalIsolationGeneration = 0;
let modalIsolationPendingFinalization = null;

function rememberBackgroundState(element) {
  return {
    element,
    hadInert: element.hasAttribute("inert"),
    inertValue: element.getAttribute("inert"),
    hadAriaHidden: element.hasAttribute("aria-hidden"),
    ariaHiddenValue: element.getAttribute("aria-hidden"),
  };
}

function restoreBackgroundState(record) {
  if (record.hadInert) record.element.setAttribute("inert", record.inertValue ?? "");
  else record.element.removeAttribute("inert");
  if (record.hadAriaHidden) record.element.setAttribute("aria-hidden", record.ariaHiddenValue ?? "");
  else record.element.removeAttribute("aria-hidden");
}

function containsActiveModal(element) {
  return [...modalIsolationOwners.keys()].some((dialog) => element === dialog || element.contains(dialog));
}

function desiredIsolatedElements() {
  const desired = new Set();
  modalIsolationOwners.forEach((owner) => {
    const { dialog } = owner;
    backgroundElements(dialog).forEach((element) => {
      if (element.isConnected && !containsActiveModal(element)) desired.add(element);
    });
  });
  return desired;
}

function refreshModalIsolation() {
  if (modalIsolationOwners.size === 0) return;
  const desired = desiredIsolatedElements();

  modalIsolationRecords.forEach((record, element) => {
    if (!desired.has(element)) {
      restoreBackgroundState(record);
      modalIsolationRecords.delete(element);
    }
  });

  desired.forEach((element) => {
    if (!modalIsolationRecords.has(element)) {
      modalIsolationRecords.set(element, rememberBackgroundState(element));
    }
    element.setAttribute("inert", "");
    element.setAttribute("aria-hidden", "true");
  });
}

function startModalIsolationObserver() {
  if (modalIsolationObserver || typeof MutationObserver === "undefined" || !document.body) return;
  modalIsolationObserver = new MutationObserver(() => refreshModalIsolation());
  modalIsolationObserver.observe(document.body, { childList: true, subtree: true });
}

function restoreAllBackgroundState() {
  modalIsolationRecords.forEach(restoreBackgroundState);
  modalIsolationRecords.clear();
}

function acquireModalIsolation(dialog, previousFocus) {
  modalIsolationGeneration += 1;
  modalIsolationPendingFinalization = null;
  if (!modalIsolationBaseFocusCaptured) {
    modalIsolationBaseFocus = previousFocus instanceof HTMLElement ? previousFocus : null;
    modalIsolationBaseFocusCaptured = true;
  }
  const lease = ++modalIsolationLease;
  modalIsolationOwners.set(dialog, { dialog, previousFocus, lease });
  startModalIsolationObserver();
  refreshModalIsolation();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const owner = modalIsolationOwners.get(dialog);
    if (!owner || owner.lease !== lease) return;
    modalIsolationOwners.delete(dialog);
    const generation = ++modalIsolationGeneration;

    if (modalIsolationOwners.size > 0) {
      refreshModalIsolation();
      const remainingOwners = [...modalIsolationOwners.values()];
      const remainingOwner = remainingOwners[remainingOwners.length - 1];
      const previousFocusIsInSurvivor = owner.previousFocus instanceof HTMLElement
        && owner.previousFocus.isConnected
        && remainingOwner.dialog.contains(owner.previousFocus);
      queueMicrotask(() => {
        if (generation !== modalIsolationGeneration || !modalIsolationOwners.has(remainingOwner.dialog)) return;
        if (previousFocusIsInSurvivor
          && owner.previousFocus.isConnected
          && remainingOwner.dialog.contains(owner.previousFocus)) owner.previousFocus.focus();
        else focusInsideDialog(remainingOwner.dialog);
      });
      return;
    }

    const pending = { generation, restoreFocus: modalIsolationBaseFocus };
    modalIsolationPendingFinalization = pending;
    queueMicrotask(() => {
      if (modalIsolationPendingFinalization !== pending
        || generation !== modalIsolationGeneration
        || modalIsolationOwners.size > 0) return;
      modalIsolationPendingFinalization = null;
      modalIsolationObserver?.disconnect();
      modalIsolationObserver = null;
      restoreAllBackgroundState();
      modalIsolationBaseFocus = null;
      modalIsolationBaseFocusCaptured = false;
      if (pending.restoreFocus instanceof HTMLElement && pending.restoreFocus.isConnected) {
        pending.restoreFocus.focus();
      }
    });
  };
}

function focusInsideDialog(dialog) {
  if (!(dialog instanceof HTMLElement) || !dialog.isConnected) return;
  if (dialog.contains(document.activeElement)) return;
  const target = dialog.querySelector("[data-admin-initial-focus='true']")
    || dialog.querySelector(FOCUSABLE_SELECTOR)
    || dialog;
  if (target instanceof HTMLElement) target.focus();
}

export function AdminEntry({ sessionStatus, session, onOpen }) {
  if (sessionStatus !== "confirmed" || session?.authenticated !== true) return null;
  return (
    <button
      type="button"
      className="adminEntryButton"
      aria-label="Admin configuration"
      title="Admin configuration"
      onClick={onOpen}
    >
      <span aria-hidden="true">⚙</span>
    </button>
  );
}

export function useAdminSession(api) {
  const [sessionStatus, setSessionStatus] = useState("checking");
  const [session, setSessionState] = useState(null);

  const setSession = useCallback((nextSession) => {
    setSessionState(nextSession);
    setSessionStatus("confirmed");
  }, []);

  useEffect(() => {
    let active = true;
    const unsubscribe = api.subscribeSessionExpired(() => {
      if (active) setSession(null);
    });
    api.getSession()
      .then((confirmedSession) => {
        if (active) setSession(confirmedSession);
      })
      .catch(() => {
        if (active) setSession(null);
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [api, setSession]);

  return { sessionStatus, session, setSession };
}

export function AdminAccess({ api }) {
  const { sessionStatus, session, setSession } = useAdminSession(api);
  const [open, setOpen] = useState(() => window.location.hash === "#admin");

  return (
    <>
      <AdminEntry sessionStatus={sessionStatus} session={session} onOpen={() => setOpen(true)} />
      {open && sessionStatus === "confirmed" && (
        <AdminPanel api={api} initialSession={session} onSessionChange={setSession} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function AdminPanel({ api, initialSession = null, onSessionChange = () => {}, onClose,
    onGuardChange, theme = "dark", openPopup, embedded = false }) {
  const dialogRef = useRef(null);
  const [session, setSession] = useState(initialSession);
  const [activeTab, setActiveTab] = useState("users");
  const [logoutError, setLogoutError] = useState("");
  const [documents, setDocuments] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [configurationError, setConfigurationError] = useState("");
  const [configurationLoading, setConfigurationLoading] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [workerRefreshVersion, setWorkerRefreshVersion] = useState(0);
  const [referenceRefreshVersion, setReferenceRefreshVersion] = useState(0);
  const [dirtyTabs, setDirtyTabs] = useState({});
  const [savingTabs, setSavingTabs] = useState({});
  const [sessionGeneration, setSessionGeneration] = useState(0);
  const sessionGenerationRef = useRef(0);
  const loadGenerationRef = useRef(0);
  const syncGenerationRef = useRef(0);
  const sessionRef = useRef(initialSession);

  const invalidateConfigurationRequests = useCallback(() => {
    sessionGenerationRef.current += 1;
    loadGenerationRef.current += 1;
    syncGenerationRef.current += 1;
  }, []);

  const transitionAuthenticatedSession = useCallback((nextSession) => {
    invalidateConfigurationRequests();
    sessionRef.current = nextSession;
    setSessionGeneration((generation) => generation + 1);
    setSession(nextSession);
    setDocuments(null);
    setSyncStatus(null);
    setConfigurationError("");
    setConfigurationLoading(false);
    setDirtyTabs({});
    setSavingTabs({});
  }, [invalidateConfigurationRequests]);

  useLayoutEffect(() => {
    transitionAuthenticatedSession(initialSession);
  }, [initialSession, transitionAuthenticatedSession]);

  useLayoutEffect(() => () => {
    invalidateConfigurationRequests();
    sessionRef.current = null;
  }, [invalidateConfigurationRequests]);

  useEffect(() => api.subscribeSessionExpired(() => {
    transitionAuthenticatedSession(null);
    onSessionChange(null);
  }), [api, onSessionChange, transitionAuthenticatedSession]);

  const loadConfigurations = useCallback(async () => {
    if (!sessionRef.current) return;
    const sessionGeneration = sessionGenerationRef.current;
    const loadGeneration = ++loadGenerationRef.current;
    const syncGeneration = ++syncGenerationRef.current;
    setConfigurationLoading(true);
    setConfigurationError("");
    try {
      const [nextDocuments, nextSyncStatus] = await Promise.all([
        api.getConfigurations(),
        api.getSyncStatus(),
      ]);
      if (sessionGeneration !== sessionGenerationRef.current
        || loadGeneration !== loadGenerationRef.current
        || !sessionRef.current) return;
      setDocuments(nextDocuments);
      if (syncGeneration === syncGenerationRef.current) setSyncStatus(nextSyncStatus);
      setDirtyTabs((current) => ({ workers: current.workers === true, "reference-packs": current["reference-packs"] === true }));
      setRefreshVersion((version) => version + 1);
    } catch (error) {
      if (sessionGeneration === sessionGenerationRef.current
        && loadGeneration === loadGenerationRef.current
        && sessionRef.current
        && error?.status !== 401) {
        setConfigurationError(error?.code === "INVALID_ADMIN_RESPONSE"
          ? "The admin service returned invalid configuration data. Refresh and try again."
          : "Admin configuration could not be loaded. Refresh and try again.");
      }
    } finally {
      if (sessionGeneration === sessionGenerationRef.current
        && loadGeneration === loadGenerationRef.current) setConfigurationLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (session) loadConfigurations();
    else {
      setDocuments(null);
      setSyncStatus(null);
      setDirtyTabs({});
      setSavingTabs({});
    }
  }, [session, loadConfigurations]);

  useLayoutEffect(() => {
    if (embedded) return undefined;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const releaseIsolation = acquireModalIsolation(dialog, previousFocus);
    return releaseIsolation;
  }, [embedded]);

  useLayoutEffect(() => {
    if (embedded) return;
    const initialFocus = dialogRef.current?.querySelector("[data-admin-initial-focus='true']");
    if (initialFocus instanceof HTMLElement) initialFocus.focus();
  }, [session, embedded]);

  const hasDirtyChanges = Object.values(dirtyTabs).some(Boolean);
  const hasSavingChanges = Object.values(savingTabs).some(Boolean);
  useEffect(() => {
    if (onGuardChange) onGuardChange((current) => current.dirty === hasDirtyChanges
      && current.saving === hasSavingChanges ? current
      : { dirty: hasDirtyChanges, saving: hasSavingChanges });
  }, [onGuardChange, hasDirtyChanges, hasSavingChanges]);
  useEffect(() => () => {
    if (onGuardChange) onGuardChange({ dirty: false, saving: false });
  }, [onGuardChange]);
  const confirmDiscardAll = () => !hasDirtyChanges
    || window.confirm("Discard all unsaved administration changes?");
  const closePanel = () => {
    if (!hasSavingChanges && onClose && confirmDiscardAll()) onClose();
  };

  const handleDialogKeyDown = (event) => {
    if (event.key === "Escape" && onClose) {
      event.preventDefault();
      if (!hasSavingChanges) closePanel();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR)]
      .filter((element) => !element.hasAttribute("aria-hidden"));
    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const activeIndex = focusable.indexOf(active);
    if (event.shiftKey && (activeIndex <= 0 || !dialogRef.current.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (activeIndex === -1 || active === last || !dialogRef.current.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  const updateSession = (nextSession) => {
    transitionAuthenticatedSession(nextSession);
    onSessionChange(nextSession);
  };

  const logout = async () => {
    if (hasSavingChanges || !confirmDiscardAll()) return;
    setLogoutError("");
    try {
      const completed = await api.logout();
      if (completed !== false) updateSession(null);
    } catch (error) {
      if (error?.status === 401) {
        updateSession(null);
      } else {
        setLogoutError(error?.message || "Logout failed. Please try again.");
      }
    }
  };

  const refreshConfigurations = () => {
    if (hasSavingChanges || !confirmDiscardAll()) return;
    if (activeTab === "workers") {
      setWorkerRefreshVersion((version) => version + 1);
      return;
    }
    if (activeTab === "reference-packs") {
      setReferenceRefreshVersion((version) => version + 1);
      return;
    }
    loadConfigurations();
  };

  const changeTab = (nextTab) => {
    if (hasSavingChanges || nextTab === activeTab) return;
    if (dirtyTabs[activeTab] && !window.confirm("Discard unsaved changes before leaving this tab?")) return;
    if (dirtyTabs[activeTab]) setDirtyTabs((current) => ({ ...current, [activeTab]: false }));
    setActiveTab(nextTab);
  };

  const refreshSyncStatus = useCallback(async () => {
    if (!sessionRef.current) return;
    const sessionGeneration = sessionGenerationRef.current;
    const syncGeneration = ++syncGenerationRef.current;
    try {
      const nextSyncStatus = await api.getSyncStatus();
      if (sessionGeneration === sessionGenerationRef.current
        && syncGeneration === syncGenerationRef.current
        && sessionRef.current) setSyncStatus(nextSyncStatus);
    } catch (error) {
      if (error?.status === 401) return;
    }
  }, [api]);

  const updateEnvelope = useCallback((key, envelope) => {
    setDocuments((current) => current ? { ...current, [key]: envelope } : current);
    setSyncStatus((current) => current ? {
      ...current,
      [key]: {
        ...(current[key] || {}),
        desiredRevision: envelope.revision,
        lastJavaError: null,
      },
    } : current);
    refreshSyncStatus();
  }, [refreshSyncStatus]);

  const dirtyHandler = useCallback((key, dirty) => {
    setDirtyTabs((current) => current[key] === dirty ? current : { ...current, [key]: dirty });
  }, []);
  const handleCustomAiDirty = useCallback((dirty) => dirtyHandler("custom-ai", dirty), [dirtyHandler]);
  const handleCodexDirty = useCallback((dirty) => dirtyHandler("codex", dirty), [dirtyHandler]);
  const handleWorkersDirty = useCallback((dirty) => dirtyHandler("workers", dirty), [dirtyHandler]);
  const handleReferencePacksDirty = useCallback((dirty) => dirtyHandler("reference-packs", dirty), [dirtyHandler]);
  const handleEventEvaluationDirty = useCallback(
    (dirty) => dirtyHandler("event-validation", dirty), [dirtyHandler],
  );
  const savingHandler = useCallback((key, saving) => {
    setSavingTabs((current) => current[key] === saving ? current : { ...current, [key]: saving });
  }, []);
  const handleCustomAiSaving = useCallback((saving) => savingHandler("custom-ai", saving), [savingHandler]);
  const handleCodexSaving = useCallback((saving) => savingHandler("codex", saving), [savingHandler]);
  const handleWorkersSaving = useCallback((saving) => savingHandler("workers", saving), [savingHandler]);
  const handleReferencePacksSaving = useCallback((saving) => savingHandler("reference-packs", saving), [savingHandler]);
  const handleEventEvaluationSaving = useCallback(
    (saving) => savingHandler("event-validation", saving), [savingHandler],
  );

  const currentTab = TABS.find(([id]) => id === activeTab) || TABS[0];

  return (
    <div
      ref={dialogRef}
      className={embedded ? "adminPanelBackdrop adminPanelEmbedded" : "adminPanelBackdrop"}
      role={embedded ? "region" : "dialog"}
      aria-modal={embedded ? undefined : "true"}
      aria-labelledby="admin-panel-title"
      tabIndex={embedded ? undefined : -1}
      onKeyDown={embedded ? undefined : handleDialogKeyDown}
    >
      <section className="adminPanel">
        <header className="adminPanelHeader">
          <div>
            <p className="adminEyebrow">Cryptember</p>
            <h1
              id="admin-panel-title"
              tabIndex={session ? -1 : undefined}
              data-admin-initial-focus={session ? "true" : undefined}
            >Insights administration</h1>
            {session && <p className="adminIdentity">{session.displayLabel || "Administrator"}</p>}
          </div>
          {onClose && (
            <button type="button" className="adminIconButton" aria-label="Close administration"
              disabled={hasSavingChanges} onClick={closePanel}>×</button>
          )}
        </header>

        {!session ? (
          <AdminLogin api={api} onAuthenticated={updateSession} />
        ) : (
          <>
            <div className="adminPanelActions">
              <button type="button" className="adminSecondaryButton" onClick={refreshConfigurations}
                disabled={hasSavingChanges} aria-label="Refresh admin configuration">Refresh</button>
              <button type="button" className="adminSecondaryButton" disabled={hasSavingChanges} onClick={logout}>Log out</button>
            </div>
            {logoutError && <div className="adminAlert adminAlertError" role="alert">{logoutError}</div>}
            <div className="adminWorkspace">
            <nav className="adminTabs" aria-label="Administration sections">
              {ADMIN_GROUPS.map((group) => (
                <div className="adminNavGroup" key={group.label}>
                  <div className="adminNavLabel">{group.label}</div>
                  {group.items.map(([id, label]) => (
                    <button key={id} id={`admin-tab-${id}`} type="button"
                      aria-current={activeTab === id ? "page" : undefined}
                      className={activeTab === id ? "adminTab adminTabActive" : "adminTab"}
                      disabled={hasSavingChanges} onClick={() => changeTab(id)}>
                      {label}{dirtyTabs[id] ? " · unsaved" : ""}
                    </button>
                  ))}
                </div>
              ))}
            </nav>
            <main
              id={`admin-panel-${currentTab[0]}`}
              className="adminTabPanel"
              role="tabpanel"
              aria-labelledby={`admin-tab-${currentTab[0]}`}
              aria-label={currentTab[1]}
            >
              <h2>{currentTab[2]}</h2>
              <AdminSectionBoundary key={activeTab}>
              {["custom-ai", "codex", "event-validation", "sync", "audit", "health"].includes(activeTab) && configurationLoading && !documents && <p role="status">Loading configuration…</p>}
              {["custom-ai", "codex", "event-validation", "sync", "audit", "health"].includes(activeTab) && configurationError && (
                <div className="adminAlert adminAlertError" role="alert">{configurationError}</div>
              )}
              {!configurationLoading && !configurationError && activeTab === "custom-ai" && documents?.customAi && (
                <CustomAiConfigurationForm
                  key={`custom-ai-${sessionGeneration}`}
                  api={api}
                  envelope={documents.customAi}
                  syncStatus={syncStatus?.customAi}
                  refreshVersion={refreshVersion}
                  sessionGeneration={sessionGeneration}
                  onDirtyChange={handleCustomAiDirty}
                  onSavingChange={handleCustomAiSaving}
                  onEnvelopeChange={updateEnvelope}
                  onRefresh={refreshConfigurations}
                />
              )}
              {!configurationLoading && !configurationError && activeTab === "codex" && documents?.codex && (
                <CodexConfigurationForm
                  key={`codex-${sessionGeneration}`}
                  api={api}
                  envelope={documents.codex}
                  syncStatus={syncStatus?.codex}
                  refreshVersion={refreshVersion}
                  sessionGeneration={sessionGeneration}
                  onDirtyChange={handleCodexDirty}
                  onSavingChange={handleCodexSaving}
                  onEnvelopeChange={updateEnvelope}
                  onRefresh={refreshConfigurations}
                />
              )}
              {!configurationLoading && !configurationError
                && activeTab === "event-validation" && documents?.eventEvaluation && (
                <EventEvaluationConfigurationForm
                  key={`event-evaluation-${sessionGeneration}`}
                  api={api}
                  envelope={documents.eventEvaluation}
                  syncStatus={syncStatus?.eventEvaluation}
                  screenshotsEnabled={documents.codex?.configuration?.screenshotsEnabled === true}
                  onOpenReferencePacks={() => changeTab("reference-packs")}
                  refreshVersion={refreshVersion}
                  sessionGeneration={sessionGeneration}
                  onDirtyChange={handleEventEvaluationDirty}
                  onSavingChange={handleEventEvaluationSaving}
                  onEnvelopeChange={updateEnvelope}
                  onRefresh={refreshConfigurations}
                />
              )}
              {!configurationLoading && !configurationError && activeTab === "sync" && documents && (
                <SyncStatusPanel documents={documents} syncStatus={syncStatus} />
              )}
              {activeTab === "workers" && (
                <WorkerPoolPanel key={`workers-${sessionGeneration}`} api={api}
                  refreshVersion={workerRefreshVersion} onRefresh={refreshConfigurations}
                  onDirtyChange={handleWorkersDirty} onSavingChange={handleWorkersSaving} />
              )}
              {activeTab === "reference-packs" && (
                <ReferencePacksPanel key={`reference-packs-${sessionGeneration}`} api={api}
                  refreshVersion={referenceRefreshVersion} onRefresh={refreshConfigurations}
                  onDirtyChange={handleReferencePacksDirty} onSavingChange={handleReferencePacksSaving} />
              )}
              {!configurationLoading && !configurationError && activeTab === "health" && (
                <HealthStatusPanel api={api} active={activeTab === "health"} />
              )}
              {!configurationLoading && !configurationError && activeTab === "audit" && (
                <AuditPanel api={api} refreshVersion={refreshVersion} />
              )}
              {activeTab === "users" && <UserRevenuePanel api={api} />}
              {activeTab === "referrals" && <ReferralAdminPanel api={api} />}
              {activeTab === "payouts" && <PayoutAdminPanel api={api} />}
              {activeTab === "pattern-research" && (
                <PatternResearchTab adminApi={api} theme={theme} openPopup={openPopup} />
              )}
              {!configurationLoading && !configurationError
                && !["users", "referrals", "payouts", "custom-ai", "codex", "event-validation", "workers", "reference-packs", "pattern-research", "health", "sync", "audit"].includes(activeTab)
                && <p>This section is ready for its configuration controls.</p>}
              </AdminSectionBoundary>
            </main>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default AdminPanel;
